import { useCallback, useEffect, useMemo } from 'react';

import { COMPONENTS_WITHOUT_PROPERTY_SUPPORT } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import {
  SelectedComponentAndStep,
  SelectedComponentTab,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import { isUiFormComponentConfig, UiComponentConfigType } from 'lib/interfaces/ui-components';

export const UPDATE_COMPONENT_PANEL_TAB_EVENT = 'updateComponentPanelSetting';

type ComponentPanelLocalStorageSetting = {
  tab: SelectedComponentTab;
  id: string;
  panelOpenForNewComponent: boolean;
};

export type ComponentPanelUpdateEvent = CustomEvent<ComponentPanelLocalStorageSetting>;

interface ComponentPanelUpdateEventListener extends EventListener {
  (evt: ComponentPanelUpdateEvent): void;
}

const updateComponentPanelSettingsPubSub = {
  subscribe: (updateListener: ComponentPanelUpdateEventListener) => {
    document?.addEventListener(UPDATE_COMPONENT_PANEL_TAB_EVENT, updateListener);
  },
  unsubscribe: (updateListener: ComponentPanelUpdateEventListener) => {
    document?.removeEventListener(UPDATE_COMPONENT_PANEL_TAB_EVENT, updateListener);
  },
  publish: (data: ComponentPanelLocalStorageSetting) => {
    const componentPanelUpdateEvent: ComponentPanelUpdateEvent = new CustomEvent(
      UPDATE_COMPONENT_PANEL_TAB_EVENT,
      { detail: data },
    );
    document?.dispatchEvent(componentPanelUpdateEvent);
  },
};

export const getSelectedComponentTabID = (componentName: string, stepName: string) =>
  `${componentName}-${stepName}`;

export const setComponentPanelSetActiveTab = ({
  componentPanelIsOpen,
  componentName,
  stepName,
  tab,
}: {
  componentPanelIsOpen: boolean;
  componentName: string;
  stepName: string;
  tab: SelectedComponentTab;
}) => {
  const newActiveTab = {
    tab,
    id: getSelectedComponentTabID(componentName, stepName),
    panelOpenForNewComponent: false,
  };
  componentPanelIsOpen
    ? updateComponentPanelSettingsPubSub.publish({
        tab,
        id: getSelectedComponentTabID(componentName, stepName),
        panelOpenForNewComponent: false,
      })
    : StorageClient.setLocalItem(
        LocalStorageKey.DashboardITEScreenEditorSelectedComponentPanelSettings,
        JSON.stringify(newActiveTab),
      );
};

/**
 * Method for providing fallback for active component panel tab
 * in cases where one is needed
 */
export const evaluateOpenTabFallbackForSelectedComponent = ({
  componentType,
  canAccessLogicTab,
  isFormFieldComponentConfig,
  panelOpenForNewComponent,
  savedTab,
}: {
  componentType?: UiComponentConfigType;
  canAccessLogicTab: boolean;
  isFormFieldComponentConfig: boolean;
  panelOpenForNewComponent: boolean;
  savedTab: SelectedComponentTab;
}) => {
  /**
   *  Default to properties tab if:
   *  1) selected component supports rendering the properties tab AND
   *     - selected right after adding a net-new one
   *     - the saved tab is FIELD
   *     - the saved tab is LOGIC when the logic tab is inaccessible
   *  2) component drag and drop is not enabled
   */
  if (componentType == null) return SelectedComponentTab.PROPERTIES;

  const componentSupportsPropertiesTab =
    !COMPONENTS_WITHOUT_PROPERTY_SUPPORT.includes(componentType);

  if (
    (panelOpenForNewComponent ||
      (!isFormFieldComponentConfig && savedTab === SelectedComponentTab.FIELD) ||
      (savedTab === SelectedComponentTab.LOGIC && !canAccessLogicTab)) &&
    componentSupportsPropertiesTab
  ) {
    return SelectedComponentTab.PROPERTIES;
  }

  if (savedTab === SelectedComponentTab.PROPERTIES && !componentSupportsPropertiesTab) {
    return SelectedComponentTab.STYLES;
  }

  return savedTab;
};

export type UseComponentEditorComponentPanelSettingsParams = {
  selectedComponentAndStep: SelectedComponentAndStep;
  canAccessLogicTab?: boolean;
  isNewComponent: boolean;
};

/**
 * Hook for managing component panel settings for FLE component editor
 * within screen editor
 */
const useComponentEditorComponentPanelSettings = ({
  selectedComponentAndStep,
  canAccessLogicTab = false,
  isNewComponent,
}: UseComponentEditorComponentPanelSettingsParams) => {
  const tabSettingID = useMemo(
    () =>
      getSelectedComponentTabID(
        selectedComponentAndStep?.component?.name ?? '',
        selectedComponentAndStep?.step?.name ?? '',
      ),
    [selectedComponentAndStep],
  );

  const isFormFieldComponentConfig = isUiFormComponentConfig(selectedComponentAndStep?.component);

  const [componentPanelSettings, setComponentPanelSettings] =
    useLocalStorage<ComponentPanelLocalStorageSetting>(
      LocalStorageKey.DashboardITEScreenEditorSelectedComponentPanelSettings,
      {
        tab: SelectedComponentTab.PROPERTIES,
        id: tabSettingID,
        panelOpenForNewComponent: false,
      },
    );

  const getDefaultOpenTab = useCallback(() => {
    const { tab, panelOpenForNewComponent } = componentPanelSettings;

    // Allow for panel to open fields tab for new components on initial drop
    // isNewComponent - denotes net-new component
    // selectedComponentAndStep.newComponent - denotes a new component that was just added
    if (
      isFormFieldComponentConfig &&
      isNewComponent &&
      selectedComponentAndStep?.newComponent === true
    ) {
      return {
        tab: SelectedComponentTab.FIELD,
        id: tabSettingID,
        panelOpenForNewComponent: true,
      };
    }

    return {
      tab: evaluateOpenTabFallbackForSelectedComponent({
        savedTab: tab,
        canAccessLogicTab,
        panelOpenForNewComponent,
        isFormFieldComponentConfig,
        componentType: selectedComponentAndStep?.component?.type,
      }),
      id: tabSettingID,
      panelOpenForNewComponent: false,
    };
  }, [
    isFormFieldComponentConfig,
    componentPanelSettings,
    isNewComponent,
    tabSettingID,
    selectedComponentAndStep,
    canAccessLogicTab,
  ]);

  useEffect(() => {
    if (componentPanelSettings.id !== tabSettingID) {
      setComponentPanelSettings(getDefaultOpenTab());
    }
  }, [setComponentPanelSettings, getDefaultOpenTab, tabSettingID, componentPanelSettings.id]);

  // add subscription listener to update panel tab
  useEffect(() => {
    const updateListener = (evt: ComponentPanelUpdateEvent) => {
      setComponentPanelSettings(evt.detail);
    };

    updateComponentPanelSettingsPubSub.subscribe(
      updateListener as ComponentPanelUpdateEventListener,
    );

    return () => {
      updateComponentPanelSettingsPubSub.unsubscribe(
        updateListener as ComponentPanelUpdateEventListener,
      );
    };
  }, []);

  return {
    componentPanelSettings,
    setComponentPanelSettings,
    tabSettingID,
  };
};

export default useComponentEditorComponentPanelSettings;
